import React, {Component} from 'react';
import PropTypes from "prop-types";
import _ from "lodash";

import SectionCard from "../Sections/SectionCard";
import ControlForm from "../Controls/ControlForm";
import NationalMapSelector from "../Charts/NationalMap/NationalMapSelector";
import StateMap from "../Charts/StateMap/StateMap";
import LegendView from "../Charts/Legend/LegendView";
import SectionModal from "../Sections/SectionModal";
import ControlExports from "../Controls/ControlExports";
import DetailTable from "../Tables/DetailTable";
import SelectionTable from "../Tables/SelectionTable";

import * as Utils from "../../utilities/utilities";

import {TEXT} from '../../utilities/strings';
import {VARIABLES, DISPLAYS} from "../../utilities/constants";

/**
 * Component to select Components for the counties section, and pass them into the SectionCard
 *
 * @example
 * <CountiesSection
 *   loading={this.props.loading}
 *   selectedState={this.props.selectedState}
 *   comparisonState={this.props.comparisonState}
 *   selectedCounty={this.props.selectedCounty}
 *   comparisonCounty={this.props.comparisonCounty}
 *   nationalData={this.props.nationalData}
 *   statesData={this.props.statesData}
 *   countiesData={this.props.countiesData}
 *   summaryType={this.props.summaryType}
 *   displayType={this.props.displayType}
 *   selectedCost={this.props.selectedCost}
 *   selectedStateFips={this.props.selectedStateFips}
 *   comparisonStateFips={this.props.comparisonStateFips}
 *   selectedCountyFips={this.props.selectedCountyFips}
 *   comparisonCountyFips={this.props.comparisonCountyFips}
 *   onExportData={this.props.exportCountiesData}
 *   onExportImage={this.props.exportCountiesImage}
 *   onExportVector={this.props.exportCountiesVector}
 *   onSelectedStateChange={this.props.setSelectedStateFips}
 *   onSelectedCountyChange={this.props.setSelectedCountyFips}
 *   onComparisonCountyChange={this.props.setComparisonCountyFips}
 *   onUpdateSelections={this.props.setFormSelections}
 *  />
 */
export default class CountiesSection extends Component {

  static propTypes = {
    loading: PropTypes.bool,
    selectedState: PropTypes.object,
    comparisonState: PropTypes.object,
    selectedCounty: PropTypes.object,
    comparisonCounty: PropTypes.object,
    nationalData: PropTypes.array,
    statesData: PropTypes.array,
    countiesData: PropTypes.array,
    summaryType: PropTypes.string,
    displayType: PropTypes.string,
    selectedCost: PropTypes.string,
    selectedStateFips: PropTypes.number,
    comparisonStateFips: PropTypes.number,
    selectedCountyFips: PropTypes.number,
    comparisonCountyFips: PropTypes.number,
    onExportData: PropTypes.func.isRequired,
    onExportImage: PropTypes.func.isRequired,
    onExportVector: PropTypes.func.isRequired,
    onSelectedStateChange: PropTypes.func.isRequired,
    onSelectedCountyChange: PropTypes.func.isRequired,
    onComparisonCountyChange: PropTypes.func.isRequired,
    onUpdateSelections: PropTypes.func.isRequired
  };

  render() {
    const tableId = 'county-table';
    const sectionType = 'county';

    // This env var controls if this section is added to the page
    const showCounty = process.env.REACT_APP_HIDE_COUNTY !== 'true'

    const hasSelectedState = !!this.props.selectedStateFips;
    const showTable = this.props.displayType !== DISPLAYS.CHART;

    const title = Utils.getCountySectionTitle(this.props.selectedCost);
    const footnote = hasSelectedState ? Utils.getSectionFootnote(this.props.selectedCost) : TEXT.county_map_prompt;

    const mapProperty = Utils.getMapValueProperty(this.props.selectedCost);
    const legendProperty = Utils.getLegendValueProperty(this.props.selectedCost);
    const legendTitle = Utils.getLegendValueTitle(this.props.selectedCost);

    const summary = (
      <div>
        <span>{TEXT.county_description}</span>
        <SectionModal title={TEXT.county_info_title} body={TEXT.county_info_body}/>
      </div>
    );

    const controls = (
      <ControlForm
        formName={sectionType}
        focusIdOnSubmit={tableId}
        showSelectedCost={true}
        showSelectedState={true}
        showSelectedCounty={true}
        showComparisonCounty={true}
        showDisplayType={true}
        countiesData={this.props.countiesData}
        statesData={this.props.statesData}
        displayType={this.props.displayType}
        summaryType={this.props.summaryType}
        selectedCost={this.props.selectedCost}
        selectedStateFips={this.props.selectedStateFips}
        comparisonStateFips={this.props.comparisonStateFips}
        selectedCountyFips={this.props.selectedCountyFips}
        comparisonCountyFips={this.props.comparisonCountyFips}
        onSubmit={this.props.onUpdateSelections}
      />
    );

    const table = (
      <DetailTable
        focusId={tableId}
        caption={footnote}
        data={this.props.countiesData}
        loading={this.props.loading}
        rowKey={VARIABLES.CountyID}
        columns={Utils.getDetailColumns(this.props.selectedCost)}
        selectedRowId={this.props.selectedCountyFips}
        comparisonRowId={this.props.comparisonCountyFips}
        onSelect={this.props.onSelectedCountyChange}
        onComparison={this.props.onComparisonCountyChange}
      />
    )

    const chart = (
      <div>
        <StateMap
          showCities={true}
          colorField={mapProperty}
          legendField={legendProperty}
          stateFips={this.props.selectedStateFips}
          selectedIds={[this.props.selectedCountyFips, this.props.comparisonCountyFips]}
          selectedCost={this.props.selectedCost}
          data={this.props.countiesData}
          dict={_.keyBy(this.props.countiesData, VARIABLES.CountyID)}
          onClick={this.props.onSelectedCountyChange}
          onAltClick={this.props.onComparisonCountyChange}
        />
        <SelectionTable
          caption={footnote}
          type={sectionType}
          nationalData={this.props.nationalData}
          primaryLocation={this.props.selectedState}
          selectedLocation={this.props.selectedCounty}
          comparisonLocation={this.props.comparisonCounty}
          columns={Utils.getSelectionColumns(this.props.selectedCost)}
        />
      </div>
    )

    const selector = (
      <NationalMapSelector
        onClick={this.props.onSelectedStateChange}
        prompt={TEXT.county_map_prompt}
      />
    )

    const exports = hasSelectedState && (
      <ControlExports
        onExportData={this.props.onExportData}
        onExportImage={this.props.onExportImage}
        onExportVector={this.props.onExportVector}
      />
    )

    const legend = hasSelectedState && (
      <LegendView
        data={this.props.countiesData}
        title={legendTitle}
        quantileField={mapProperty}
        valueField={legendProperty}
      />
    )

    return showCounty && (
      <SectionCard
        section={sectionType}
        title={title}
        exports={exports}
        summary={summary}
        controls={controls}
        legend={legend}
        content={hasSelectedState ? chart : selector}
        alternate={table}
        showAlternate={showTable}
      />
    );
  }
}
